import React from 'react';
import { StandardTableActions, Badge, ColoredBadge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { ClientService } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, formatDateForFilter, APP_CONFIG } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import { RenewalRemindersSection } from './RenewalRemindersSection';
import { renderRenewalStatusBadge } from '../service-renewal-reminders/config';

/** True if end date (ISO date string) is within 15 days from today or already passed. */
function isEndDateWithin15Days(endDateStr: string | undefined | null): boolean {
  if (!endDateStr) return false;
  const end = new Date(endDateStr);
  if (isNaN(end.getTime())) return false;
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const endDay = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  const oneDay = 24 * 60 * 60 * 1000;
  const daysUntil = (endDay - today) / oneDay;
  return daysUntil <= 15;
}

const renderServiceTypeBadge = (label?: string, color?: string) => (
  <ColoredBadge label={label} color={color} fallback="-" />
);

const renderServiceTagBadge = (tag: { name?: string; color?: string }, key: React.Key) => (
  <ColoredBadge key={key} label={tag.name || 'Tag'} color={tag.color} />
);

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Client Services',
    description: 'Manage client services database',
    permissionModuleKey: 'client_services',
    route: {
      list: '/client-services',
      new: '/client-services/new',
      detail: (id: number) => `/client-services/${id}`,
      edit: (id: number) => `/client-services/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients-services',
      detail: (id: number) => `/crm-clients-services/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search services by client name, type, or category...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientId', 'serviceType', 'serviceCategory', 'serviceDescr', 'serviceActive', 'serviceHasReminder', 'serviceTags',
        'serviceFreq', 'serviceFreqType'
      ],
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: [
        'serviceStartDate', 'serviceEndDate', 'serviceTerminationDate', 'serviceCreatedDate'
      ],
    },
    {
      name: 'financial',
      label: 'Financial',
      icon: '💰',
      fields: [
        'serviceCost', 'serviceInvoice', 'serviceProfit'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: ClientService) =>
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'serviceType',
        label: 'Service Type',
        placeholder: 'All Service Types',
        type: 'select' as const,
        getValue: (item: ClientService) =>
          item.serviceTypeDescr?.pst_descr || (item.serviceType ? `Type #${item.serviceType}` : ''),
        options: [],
      },
      {
        key: 'serviceCategory',
        label: 'Service Category',
        placeholder: 'All Categories',
        type: 'select' as const,
        getValue: (item: ClientService) =>
          item.serviceCategoryDescr?.psc_descr || (item.serviceCategory ? `Category #${item.serviceCategory}` : ''),
        options: [],
      },
      {
        key: 'serviceDescr',
        label: 'Description',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: ClientService) => item.serviceDescr,
        options: [],
      },
      {
        key: 'serviceActive',
        label: 'Status',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: ClientService) => item.serviceActive ? 'Active' : 'Inactive',
        options: ['Active', 'Inactive'],
      },
      {
        key: 'serviceHasReminder',
        label: 'Reminder',
        placeholder: 'All',
        type: 'boolean' as const,
        getValue: (item: ClientService) =>
          (item.renewalReminders?.length ?? 0) > 0 ? 'Has Reminder' : 'No Reminder',
        options: ['Has Reminder', 'No Reminder'],
      },
      {
        key: 'serviceStartDate',
        label: 'Start Date',
        placeholder: 'All Start Dates',
        type: 'date' as const,
        getValue: (item: ClientService) =>
          item.serviceStartDate
            ? formatDateForFilter(item.serviceStartDate)
            : undefined,
        options: [],
      },
      {
        key: 'serviceEndDate',
        label: 'End Date',
        placeholder: 'All End Dates',
        type: 'date' as const,
        getValue: (item: ClientService) =>
          item.serviceEndDate
            ? formatDateForFilter(item.serviceEndDate)
            : undefined,
        options: [],
      },
      {
        key: 'serviceTerminationDate',
        label: 'Termination Date',
        placeholder: 'All Termination Dates',
        type: 'date' as const,
        getValue: (item: ClientService) =>
          item.serviceTerminationDate
            ? formatDateForFilter(item.serviceTerminationDate)
            : undefined,
        options: [],
      },
      {
        key: 'serviceCreatedDate',
        label: 'Created Date',
        placeholder: 'All Created Dates',
        type: 'date' as const,
        getValue: (item: ClientService) =>
          item.serviceCreatedDate
            ? formatDateForFilter(item.serviceCreatedDate)
            : undefined,
        options: [],
      },
      {
        key: 'serviceCost',
        label: 'Cost',
        placeholder: 'All Costs',
        type: 'number' as const,
        getValue: (item: ClientService) => item.serviceCost?.toString(),
        options: [],
      },
      {
        key: 'serviceInvoice',
        label: 'Invoice',
        placeholder: 'All Invoices',
        type: 'number' as const,
        getValue: (item: ClientService) => item.serviceInvoice?.toString(),
        options: [],
      },
      {
        key: 'serviceProfit',
        label: 'Profit',
        placeholder: 'All Profits',
        type: 'number' as const,
        getValue: (item: ClientService) => item.serviceProfit?.toString(),
        options: [],
      },
      {
        key: 'serviceFreq',
        label: 'Frequency',
        placeholder: 'All Frequencies',
        type: 'number' as const,
        getValue: (item: ClientService) => item.serviceFreq?.toString(),
        options: [],
      },
      {
        key: 'serviceFreqType',
        label: 'Frequency Type',
        placeholder: 'All Frequency Types',
        type: 'select' as const,
        getValue: (item: ClientService) =>
          item.serviceFreqTypeDescr?.name || (item.serviceFreqType ? `Freq Type #${item.serviceFreqType}` : ''),
        options: [],
      },
      {
        key: 'serviceTags',
        label: 'Tags',
        placeholder: 'All Tags',
        type: 'select' as const,
        getValue: (item: ClientService) => {
          const tags = item.serviceTags || [];
          return tags.map((tag: any) => tag.name).join(', ') || '';
        },
        options: [],
      },
    ],
    applyFilter: (
      items: ClientService[],
      filters: {
        clientId: string[];
        serviceType: string[];
        serviceCategory: string[];
        serviceDescr: string[] | string;
        serviceActive: string[];
        serviceHasReminder: string[];
        serviceStartDate: string[];
        serviceEndDate: string[];
        serviceTerminationDate: string[];
        serviceCreatedDate: string[];
        serviceCost: string[];
        serviceInvoice: string[];
        serviceProfit: string[];
        serviceFreq: string[];
        serviceFreqType: string[];
        serviceTags: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Client filter
        if (filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter ||
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Service Type filter
        if (filters.serviceType.length > 0) {
          const displayValue = item.serviceTypeDescr?.pst_descr || (item.serviceType ? `Type #${item.serviceType}` : '');
          const matches = filters.serviceType.some((filter) => {
            return displayValue === filter ||
                   (item.serviceType && filter === `Type #${item.serviceType}`) ||
                   (item.serviceTypeDescr?.pst_descr && filter === item.serviceTypeDescr.pst_descr);
          });
          if (!matches) return false;
        }

        // Service Category filter
        if (filters.serviceCategory.length > 0) {
          const displayValue = item.serviceCategoryDescr?.psc_descr || (item.serviceCategory ? `Category #${item.serviceCategory}` : '');
          const matches = filters.serviceCategory.some((filter) => {
            return displayValue === filter ||
                   (item.serviceCategory && filter === `Category #${item.serviceCategory}`) ||
                   (item.serviceCategoryDescr?.psc_descr && filter === item.serviceCategoryDescr.psc_descr);
          });
          if (!matches) return false;
        }

        // Description filter (text filter)
        const descrFilter = filters.serviceDescr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.serviceDescr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.serviceDescr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Active status filter
        if (filters.serviceActive.length > 0) {
          const status = item.serviceActive ? 'Active' : 'Inactive';
          if (!filters.serviceActive.includes(status)) return false;
        }

        // Reminder filter (Has Reminder / No Reminder)
        if (filters.serviceHasReminder.length > 0) {
          const hasReminder = (item.renewalReminders?.length ?? 0) > 0;
          const wantHasReminder = filters.serviceHasReminder.includes('Has Reminder');
          const wantNoReminder = filters.serviceHasReminder.includes('No Reminder');
          if (wantHasReminder && !hasReminder) return false;
          if (wantNoReminder && hasReminder) return false;
        }

        // Start Date filter (formatDateForFilter = locale-independent, works on Linux/Docker)
        if (filters.serviceStartDate.length > 0) {
          if (ranges.serviceStartDate) {
            const range = ranges.serviceStartDate;
            if (range.start && range.end && item.serviceStartDate) {
              const itemDate = new Date(item.serviceStartDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.serviceStartDate
              ? formatDateForFilter(item.serviceStartDate)
              : '';
            const matches = filters.serviceStartDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // End Date filter
        if (filters.serviceEndDate.length > 0) {
          if (ranges.serviceEndDate) {
            const range = ranges.serviceEndDate;
            if (range.start && range.end && item.serviceEndDate) {
              const itemDate = new Date(item.serviceEndDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.serviceEndDate
              ? formatDateForFilter(item.serviceEndDate)
              : '';
            const matches = filters.serviceEndDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Termination Date filter
        if (filters.serviceTerminationDate.length > 0) {
          if (ranges.serviceTerminationDate) {
            const range = ranges.serviceTerminationDate;
            if (range.start && range.end && item.serviceTerminationDate) {
              const itemDate = new Date(item.serviceTerminationDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.serviceTerminationDate
              ? formatDateForFilter(item.serviceTerminationDate)
              : '';
            const matches = filters.serviceTerminationDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Created Date filter
        if (filters.serviceCreatedDate.length > 0) {
          if (ranges.serviceCreatedDate) {
            const range = ranges.serviceCreatedDate;
            if (range.start && range.end && item.serviceCreatedDate) {
              const itemDate = new Date(item.serviceCreatedDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.serviceCreatedDate
              ? formatDateForFilter(item.serviceCreatedDate)
              : '';
            const matches = filters.serviceCreatedDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Cost filter
        if (filters.serviceCost.length > 0) {
          const value = item.serviceCost?.toString() || '';
          const matches = filters.serviceCost.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Invoice filter
        if (filters.serviceInvoice.length > 0) {
          const value = item.serviceInvoice?.toString() || '';
          const matches = filters.serviceInvoice.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Profit filter
        if (filters.serviceProfit.length > 0) {
          const value = item.serviceProfit?.toString() || '';
          const matches = filters.serviceProfit.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Frequency filter
        if (filters.serviceFreq.length > 0) {
          const value = item.serviceFreq?.toString() || '';
          const matches = filters.serviceFreq.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Frequency Type filter
        if (filters.serviceFreqType.length > 0) {
          const displayValue = item.serviceFreqTypeDescr?.name || (item.serviceFreqType ? `Freq Type #${item.serviceFreqType}` : '');
          const matches = filters.serviceFreqType.some((filter) => {
            return displayValue === filter ||
                   (item.serviceFreqType && filter === `Freq Type #${item.serviceFreqType}`) ||
                   (item.serviceFreqTypeDescr?.name && filter === item.serviceFreqTypeDescr.name);
          });
          if (!matches) return false;
        }

        // Tags filter
        if (filters.serviceTags.length > 0) {
          const itemTags = item.serviceTags || [];
          const itemTagNames = itemTags.map((tag: any) => tag.name || '').filter(Boolean);
          const hasTags = itemTagNames.length > 0;
          
          // Separate inclusions and exclusions
          const inclusions = filters.serviceTags.filter(f => !f.startsWith('!'));
          const exclusions = filters.serviceTags.filter(f => f.startsWith('!')).map(f => f.substring(1));
          
          // OR logic: item matches if ANY condition is true
          let matches = false;
          
          // Condition 1: Check for "(Empty)" inclusion
          if (inclusions.includes('(Empty)') && !hasTags) {
            matches = true;
          }
          
          // Condition 2: Check for regular inclusions (items that have these tags)
          if (!matches && inclusions.length > 0) {
            const nonEmptyInclusions = inclusions.filter(f => f !== '(Empty)');
            if (nonEmptyInclusions.length > 0) {
              matches = nonEmptyInclusions.some((filter) => {
                return itemTagNames.some((tagName: string) => 
                  tagName.toLowerCase() === filter.toLowerCase() || 
                  tagName.toLowerCase().includes(filter.toLowerCase()) ||
                  filter.toLowerCase().includes(tagName.toLowerCase())
                );
              });
            }
          }
          
          // Condition 3: Check for exclusions (items that DON'T have these tags)
          // This works with OR: if item doesn't have excluded tag, it matches
          if (exclusions.length > 0) {
            const hasExcludedTag = exclusions.some((exclusion) => {
              return itemTagNames.some((tagName: string) => 
                tagName.toLowerCase() === exclusion.toLowerCase() || 
                tagName.toLowerCase().includes(exclusion.toLowerCase()) ||
                exclusion.toLowerCase().includes(tagName.toLowerCase())
              );
            });
            
            // If item doesn't have any excluded tag, it matches
            if (!hasExcludedTag) {
              matches = true;
            }
          }
          
          if (!matches) return false;
        }

        return true;
      });
    },
  },

  // Toolbar Configuration
  toolbar: {
    viewToggle: {
      enabled: true,
      views: [
        { key: 'list', label: 'List', icon: 'list', href: '/client-services' },
        { key: 'renewal', label: 'Renewal Reminders', icon: 'reminder', href: '/service_renewal_reminders' },
        { key: 'yearly', label: 'Services', icon: 'chart', href: '/client-services-yearly' },
      ],
    },
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Service',
        showTitle: false,
        variant: 'primary' as const,
        href: '/client-services/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: ClientService) => {
      window.location.href = `/client-services/${item.clientServicetId}`;
    },
    keyExtractor: (item: ClientService) => item.clientServicetId,
    getClientId: (item: ClientService) => item.clientId ?? null,
  },

  defaultSort: { key: 'serviceEndDate', direction: 'asc' as const },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new client service.',
    noSearchResults: 'No client services found matching your search criteria.',
    noFilterResults: 'No client services match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Client Service',
    message: 'Are you sure you want to delete this client service? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete client service. Please try again.',
    handler: async (id: number) => {
      const { deleteClientService } = await import('../../services/mutations');
      await deleteClientService(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Client Service Details',
    notFoundTitle: 'Client service not found',
    notFoundMessage: 'The client service you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Client Services',
    defaultTitle: 'Unnamed Service',
    getTitle: (item: ClientService) => {
      return item.serviceDescr || item.serviceTypeDescr?.pst_descr || `Service #${item.clientServicetId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Client Service',
      subtitle: 'Add a new client service to your database',
      submitText: 'Create Service',
    },
    edit: {
      title: 'Edit Client Service',
      submitText: 'Update Service',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading service...',
    errorMessage: 'Failed to save client service. Please try again.',
    create: async (data: Partial<ClientService>) => {
      const { createClientService } = await import('../../services/mutations');
      return await createClientService(data);
    },
    update: async (id: number, data: Partial<ClientService>) => {
      const { updateClientService } = await import('../../services/mutations');
      return await updateClientService(id, data);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      required: true,
      placeholder: 'Select a client',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'serviceType',
      label: 'Service Type',
      type: 'select',
      required: true,
      placeholder: 'Select service type',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-service-type', // API endpoint to fetch all service type options
      labelField: 'pst_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'serviceCategory',
      label: 'Service Category',
      type: 'select',
      placeholder: 'Select service category',
      required: true,
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-service-category', // API endpoint to fetch all service category options
      labelField: 'psc_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'serviceDescr',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter service description',
      rows: 2,
      validation: {
        maxLength: 1000,
      },
    },
    {
      name: 'serviceStartDate',
      label: 'Start Date',
      type: 'date',
      required: true,
      placeholder: 'Select start date',
      validation: {
        required: true,
      },
    },
    {
      name: 'serviceEndDate',
      label: 'End Date',
      type: 'date',
      required: true,
      placeholder: 'Select end date',
      validation: {
        required: true,
      },
    },
    {
      name: 'serviceTerminationDate',
      label: 'Termination Date',
      type: 'date',
      placeholder: 'Select termination date',
    },
    {
      name: 'serviceFreq',
      label: 'Frequency',
      type: 'number',
      placeholder: 'Enter frequency',
      validation: {
        min: 0,
      },
    },
    {
      name: 'serviceFreqType',
      label: 'Frequency Type',
      type: 'select',
      placeholder: 'Select frequency type (optional)',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'serviceActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Mark as active',
      description: 'Check if this service is active',
      labelPosition: 'left',
      defaultValue: true,
    },
    {
      name: 'serviceCost',
      label: 'Cost',
      type: 'number',
      placeholder: 'Enter cost',
      validation: {
        min: 0,
      },
    },
    {
      name: 'serviceInvoice',
      label: 'Invoice Amount',
      type: 'number',
      placeholder: 'Enter invoice amount',
      validation: {
        min: 0,
      },
    },
    {
      name: 'serviceProfit',
      label: 'Profit',
      type: 'number',
      placeholder: 'Enter profit',
      validation: {
        min: 0,
      },
    },
    {
      name: 'serviceNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter notes',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'serviceCommonNote',
      label: 'Common Note',
      type: 'textarea',
      placeholder: 'Enter common note',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'serviceTags',
      label: 'Tags',
      type: 'multiselect',
      placeholder: 'Select tags (optional)',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/tags', // API endpoint to fetch all tag options
      labelField: 'name', // Field name in API response to use as label
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📋',
        gridCols: 'md:grid-cols-12',
        fields: ['clientId', 'serviceType', 'serviceCategory', 'serviceTags', 'serviceDescr'],
        fieldSpans: {
          clientId: 'md:col-span-6',
          serviceType: 'md:col-span-6',
          serviceCategory: 'md:col-span-6',
          serviceTags: 'md:col-span-6',
          serviceDescr: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'dates',
        title: 'Date Information',
        icon: '📅',
        gridCols: 'md:grid-cols-2',
        fields: ['serviceStartDate', 'serviceEndDate', 'serviceTerminationDate'],
        collapsible: true,
      },
      {
        id: 'frequency',
        title: 'Frequency Information',
        icon: '🔄',
        gridCols: 'md:grid-cols-2',
        fields: ['serviceFreq', 'serviceFreqType', 'serviceActive'],
        collapsible: true,
      },
      {
        id: 'financial',
        title: 'Financial Information',
        icon: '💰',
        gridCols: 'md:grid-cols-2',
        fields: ['serviceCost', 'serviceInvoice', 'serviceProfit'],
        collapsible: true,
      },
      {
        id: 'notes',
        title: 'Notes',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['serviceNotes', 'serviceCommonNote'],
        fieldSpans: {
          serviceNotes: 'md:col-span-6',
          serviceCommonNote: 'md:col-span-6',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

export type ClientServicesListContext = {
  onOpenRenewalRemindersPanel?: (item: ClientService) => void;
};

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string,
  _a?: unknown,
  colors?: { textMuted?: string },
  listContext?: ClientServicesListContext
): Column<ClientService>[] => [
  {
    key: 'serviceDescr',
    header: 'Description',
    sortable: true,
    width: '280px', // change this to set Description column width (e.g. '200px', '20%', 'minmax(200px, 1fr)')
    render: (value, row) => {
      const text = value ? (value.length > 40 ? value.substring(0, 40) + '...' : value) : '-';
      const rawClientName = row.client?.clientName || (row.clientId ? `Client #${row.clientId}` : '');
      const clientName = rawClientName.length > 30 ? rawClientName.substring(0, 30) + '...' : rawClientName;
      const tags = row.serviceTags || [];
      return (
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {tags.map((tag: { name?: string; color?: string }, index: number) =>
                  renderServiceTagBadge(tag, index)
                )}
              </div>
            )}
            {tags.length > 0 && text !== '-' && <span className="text-gray-400">·</span>}
            <span className="text-sm text-gray-900 dark:text-gray-100">{text}</span>
          </div>
          {clientName && (
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate" title={rawClientName}>
              {clientName}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'serviceType',
    header: 'Service Type',
    sortable: true,
    render: (_value, row) => renderServiceTypeBadge(row.serviceTypeDescr?.pst_descr, row.serviceTypeDescr?.pst_color),
  },
  {
    key: 'serviceCategory',
    header: 'Category',
    sortable: true,
    render: (value, row) => (
      row.serviceCategoryDescr?.psc_descr || '-'
    ),
  },
  {
    key: 'serviceStartDate',
    header: 'Start Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'serviceEndDate',
    header: 'End Date',
    sortable: true,
    render: (value, row) => {
      const endDateStr = row.serviceEndDate || row.serviceTerminationDate;
      const formatted = formatDate(endDateStr || value);
      if (!formatted) return '-';
      const showWarning = isEndDateWithin15Days(endDateStr || value);
      return (
        <span className="inline-flex items-center gap-1.5">
          {showWarning && (
            <svg className="w-4 h-4 shrink-0 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="End date within 15 days">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          <span>{formatted}</span>
        </span>
      );
    },
  },
  {
    key: 'renewalReminders',
    header: 'Renewal Reminders',
    sortable: false,
    render: (_value, item) => {
      const reminders = item.renewalReminders ?? [];
      const count = reminders.length;
      const last = reminders[0]; // backend orders by sentAt desc
      const lastDate = last?.sentAt ? formatDate(last.sentAt) : null;
      const lastStatusValue = last?.renewalResponseStatus ?? null;
      const onOpenPanel = listContext?.onOpenRenewalRemindersPanel;
      if (count === 0) {
        if (onOpenPanel) {
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onOpenPanel(item);
              }}
              className="text-indigo-600 hover:text-indigo-800 hover:underline text-left"
            >
              0 reminders
            </button>
          );
        }
        return '-';
      }
      // Lead with client response status, then response date
      const responseDate = last?.renewalResponseAt ? formatDate(last.renewalResponseAt) : null;
      const responseLine = lastStatusValue ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {renderRenewalStatusBadge(lastStatusValue)}
          {responseDate && (
            <span className={cn('text-xs', colors?.textMuted ?? 'text-gray-500')}>{responseDate}</span>
          )}
        </div>
      ) : (
        <div className={cn('text-xs', colors?.textMuted ?? 'text-gray-500')}>—</div>
      );
      const sentLine = lastDate && (
        <div className={cn('text-xs mt-0.5', colors?.textMuted ?? 'text-gray-500')}>
          Sent {lastDate} · {count} reminder{count !== 1 ? 's' : ''}
        </div>
      );
      if (onOpenPanel) {
        return (
          <div className="text-left">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onOpenPanel(item);
              }}
              className="text-left w-full hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded px-1 -mx-1 py-0.5"
            >
              {responseLine}
              {sentLine}
            </button>
          </div>
        );
      }
      return (
        <div className="text-left">
          {responseLine}
          {sentLine}
        </div>
      );
    },
  },
  {
    key: 'serviceCost',
    header: 'Cost',
    sortable: true,
    render: (value) => (
      value !== undefined && value !== null ? `$${value.toFixed(2)}` : '-'
    ),
  },
  {
    key: 'serviceInvoice',
    header: 'Invoice',
    sortable: true,
    render: (value) => (
      value !== undefined && value !== null ? `$${value.toFixed(2)}` : '-'
    ),
  },
  {
    key: 'serviceProfit',
    header: 'Profit',
    sortable: true,
    render: (value) => (
      value !== undefined && value !== null ? `$${value.toFixed(2)}` : '-'
    ),
  },

];

// Helper function to get detail sections
export const getDetailSections = (
  item: ClientService,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '-'),
        },
        {
          label: 'Service Type',
          value: renderServiceTypeBadge(item.serviceTypeDescr?.pst_descr, item.serviceTypeDescr?.pst_color),
        },
        {
          label: 'Service Category',
          value: item.serviceCategoryDescr?.psc_descr || '-',
        },
        {
          label: 'Description',
          value: item.serviceDescr || '-',
        },
        {
          label: 'Status',
          value: item.serviceActive ? 'Active' : 'Inactive',
        },
        {
          label: 'Tags',
          value: item.serviceTags && item.serviceTags.length > 0 ? (
            <div className="flex flex-wrap gap-1 mt-1">
              {item.serviceTags.map((tag: { name?: string; color?: string }, index: number) =>
                renderServiceTagBadge(tag, index)
              )}
            </div>
          ) : '-',
        },
      ],
    },
    {
      title: 'Date Information',
      icon: '📅',
      fields: [
        {
          label: 'Start Date',
          value: formatDate(item.serviceStartDate),
        },
        {
          label: 'End Date',
          value: formatDate(item.serviceEndDate),
        },
        {
          label: 'Termination Date',
          value: formatDate(item.serviceTerminationDate),
        },
        {
          label: 'Created Date',
          value: formatDate(item.serviceCreatedDate),
        },
        {
          label: 'Updated Date',
          value: formatDate(item.serviceUpdatedAt),
        },
      ],
    },
    {
      title: 'Frequency Information',
      icon: '🔄',
      fields: [
        {
          label: 'Frequency',
          value: item.serviceFreq || '-',
        },
        {
          label: 'Frequency Type',
          value: item.serviceFreqTypeDescr?.name || '-',
        },
      ],
    },
    {
      title: 'Financial Information',
      icon: '💰',
      fields: [
        {
          label: 'Cost',
          value: item.serviceCost !== undefined && item.serviceCost !== null ? `$${item.serviceCost.toFixed(2)}` : '-',
        },
        {
          label: 'Invoice Amount',
          value: item.serviceInvoice !== undefined && item.serviceInvoice !== null ? `$${item.serviceInvoice.toFixed(2)}` : '-',
        },
        {
          label: 'Profit',
          value: item.serviceProfit !== undefined && item.serviceProfit !== null ? `$${item.serviceProfit.toFixed(2)}` : '-',
        },
      ],
    },
    {
      title: 'Notes',
      icon: '📝',
      fields: [
        {
          label: 'Notes',
          value: item.serviceNotes || '-',
        },
        {
          label: 'Common Note',
          value: item.serviceCommonNote || '-',
        },
      ],
    },
    {
      title: 'Renewal reminders',
      icon: '📧',
      fields: [
        {
          label: '',
          value: <RenewalRemindersSection clientServiceId={item.clientServicetId} />,
        },
      ],
    },
    {
      title: 'System Information',
      icon: '⚙️',
      fields: [
        {
          label: 'Created By',
          value: item.serviceCreatedUser?.userFirstName || '-',
        },
        {
          label: 'Updated By',
          value: item.serviceUpdatedUser?.userFirstName || '-',
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: ClientService) => {
  return (
    <>
      <Badge variant={item.serviceActive ? 'success' : 'danger'}>
        {item.serviceActive ? 'Active' : 'Inactive'}
      </Badge>
    </>
  );
};

// Helper function to get toolbar icons
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'calendar':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case 'list':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      );
    case 'reminder':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.5 4.5v5h5m10 10v-5h-5m4.5-5a7.5 7.5 0 00-12.53-3.98M5 14.5a7.5 7.5 0 0012.53 3.98"
          />
        </svg>
      );
    case 'chart':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to get table actions
export const getTableActions = (
  item: ClientService,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.clientServicetId)}
      editTo={moduleConfig.module.route.edit(item.clientServicetId)}
      onDelete={() => onDelete(item.clientServicetId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<ClientService> => {
  return {
    clientId: undefined,
    serviceType: undefined,
    serviceCategory: undefined,
    serviceDescr: '',
    serviceStartDate: undefined,
    serviceEndDate: undefined,
    serviceTerminationDate: undefined,
    serviceFreq: undefined,
    serviceFreqType: undefined,
    serviceActive: true,
    serviceCost: undefined,
    serviceInvoice: undefined,
    serviceProfit: undefined,
    serviceNotes: '',
    serviceCommonNote: '',
    serviceTags: [],
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: ClientService): Partial<ClientService> => {
  // Convert tags array to array of IDs for the multiselect
  const tagIds = item.serviceTags?.map((tag: any) => tag.id) || [];
  
  return {
    clientId: item.clientId,
    serviceType: item.serviceType,
    serviceCategory: item.serviceCategory,
    serviceDescr: item.serviceDescr || '',
    serviceStartDate: item.serviceStartDate,
    serviceEndDate: item.serviceEndDate,
    serviceTerminationDate: item.serviceTerminationDate,
    serviceFreq: item.serviceFreq,
    serviceFreqType: item.serviceFreqType,
    serviceActive: item.serviceActive ?? true,
    serviceCost: item.serviceCost,
    serviceInvoice: item.serviceInvoice,
    serviceProfit: item.serviceProfit,
    serviceNotes: item.serviceNotes || '',
    serviceCommonNote: item.serviceCommonNote || '',
    serviceTags: tagIds,
  };
};

